import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import { useAuth as useClerkAuth } from '@clerk/clerk-react';
import { clearCachedToken, fetchClerkToken } from '../services/clerkToken';

interface User {
  id: number;
  email: string;
  full_name: string;
  avatar_url?: string;
  role: Role;
  organization: Organization;
  is_verified: boolean;
}

interface Role {
  id: number;
  name: string;
  display_name: string;
  permissions: Permission[];
}

interface Permission {
  id: number;
  name: string;
  resource: string;
  action: string;
}

interface Organization {
  id: number;
  name: string;
  slug: string;
  subscription_tier: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  logout: () => Promise<void>;
  hasPermission: (resource: string, action: string) => boolean;
  hasRole: (roleName: string) => boolean;
  updateUser: (userData: Partial<User>) => void;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL
  ? `${import.meta.env.VITE_API_BASE_URL}/api/v1`
  : '/api/v1';

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Identity comes from Clerk; authorization (role/permissions/org) comes from
 * the local backend. On a valid Clerk session we fetch the mapped local
 * profile from `/api/v1/profile`, so all existing RBAC checks keep working.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const { isLoaded, isSignedIn, getToken, signOut } = useClerkAuth();
  const [user, setUser] = useState<User | null>(null);
  const [profileChecked, setProfileChecked] = useState(false);

  const loadProfile = useCallback(async () => {
    const token = await getToken();
    if (!token) {
      throw new Error('No Clerk session token available');
    }
    localStorage.setItem('access_token', token);

    const response = await fetch(`${API_BASE_URL}/profile`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
      throw new Error('Failed to load profile');
    }

    const data = await response.json();
    setUser(data);
  }, [getToken]);

  // Load the local profile whenever the Clerk session state settles.
  useEffect(() => {
    let cancelled = false;

    const sync = async () => {
      if (!isLoaded) return;

      if (isSignedIn) {
        try {
          await loadProfile();
        } catch (error) {
          console.error('Failed to load user profile:', error);
        } finally {
          if (!cancelled) setProfileChecked(true);
        }
      } else {
        setUser(null);
        clearCachedToken();
        setProfileChecked(true);
      }
    };

    sync();
    return () => {
      cancelled = true;
    };
  }, [isLoaded, isSignedIn, loadProfile]);

  // Keep the cached token fresh for non-React API callers (v2 services, SSE).
  useEffect(() => {
    if (!isSignedIn) return;
    fetchClerkToken();
    const interval = setInterval(() => {
      fetchClerkToken();
    }, 40 * 1000);
    return () => clearInterval(interval);
  }, [isSignedIn]);

  const logout = useCallback(async () => {
    clearCachedToken();
    setUser(null);
    await signOut({ redirectUrl: '/' });
  }, [signOut]);

  const hasPermission = useCallback(
    (resource: string, action: string): boolean => {
      if (!user || !user.role) return false;
      return user.role.permissions.some(
        (perm) => perm.resource === resource && perm.action === action
      );
    },
    [user]
  );

  const hasRole = useCallback(
    (roleName: string): boolean => {
      if (!user || !user.role) return false;
      return user.role.name === roleName;
    },
    [user]
  );

  const updateUser = useCallback((userData: Partial<User>) => {
    setUser((prev) => (prev ? { ...prev, ...userData } : prev));
  }, []);

  const isLoading = !isLoaded || (!!isSignedIn && !profileChecked);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!isSignedIn && !!user,
    isLoading,
    logout,
    hasPermission,
    hasRole,
    updateUser,
    refreshProfile: loadProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
